import { construireReference, construireIdentifiantEtude } from './utilitairesReference.js';

export function estGenreFeminin(genre) {
  return ['F', 'Femme', 'Mme'].includes(genre);
}

/**
 * Met en majuscule la première lettre d'un mot.
 * Utilisé pour fournir, à côté de chaque déterminant/nom genré ("le", "la",
 * "client", "cliente"...), une variante avec majuscule initiale — car on ne
 * peut pas savoir depuis les données si le mot va tomber en début de phrase
 * dans le document Word (une même balise peut être utilisée à plusieurs
 * endroits). C'est donc à toi de choisir, dans Word, la balise "Maj" ou pas
 * selon ce qui précède : point, saut de paragraphe → variante "Maj" ;
 * au milieu d'une phrase → variante normale.
 */
export function capitaliser(mot) {
  if (!mot) return mot;
  return mot.charAt(0).toUpperCase() + mot.slice(1);
}

/**
 * Données communes à TOUS les documents (Convention d'Étude, Devis, PVRI,
 * PVRF...) : identité du client, représentation i2c (président ou
 * co-présidents), déterminants genrés, référence du document.
 *
 * @param {object} formData
 * @param {string} prefixeDocument  'CE' | 'Devis' | 'PVRI' | 'PVRF'...
 * @param {number} numDoc  le numéro (commun.prochainNumDoc) attribué à CETTE
 *   génération. Fourni par la vue qui appelle le générateur (voir
 *   composants/vues/VueXxx.js), pas lu directement dans formData, car il doit
 *   être figé au moment de la génération.
 *
 * Chaque générateur de document spread le résultat de cette fonction dans
 * son propre objet de données, puis ajoute uniquement ses champs
 * spécifiques. Ça évite de dupliquer cette logique dans 4 à 6 fichiers.
 */
export function construireDonneesCommunes(formData, prefixeDocument, numDoc) {
  const { infosGenerales, client, i2c } = formData.commun;

  const identifiantEtude = construireIdentifiantEtude(infosGenerales);
  const refDocument = construireReference(prefixeDocument, identifiantEtude, numDoc);

  // La Convention d'Étude a son PROPRE numDoc, figé lors de sa dernière
  // génération (formData.documents.conventionEtude.numDoc) — ce n'est pas le
  // même numéro que celui du document en cours de génération.
  const numDocConventionEtude = formData.documents.conventionEtude?.numDoc;
  const refConventionEtude = numDocConventionEtude
    ? construireReference('CE', identifiantEtude, numDocConventionEtude)
    : ''; // la Convention d'Étude n'a pas encore été générée

  const estEntreprise = Boolean(client.nomEntreprise && client.nomEntreprise.trim());
  const identiteClient = estEntreprise
    ? client.nomEntreprise
    : `${client.prenom} ${client.nom}`.trim();
  const ligneSiret = estEntreprise ? client.siret : '';

  const estClientFeminin = estGenreFeminin(client.genre);
  const detClient = estClientFeminin ? 'la' : 'le';
  const detClientMaj = capitaliser(detClient);
  const detAuClient = estClientFeminin ? 'à la' : 'au';
  const nomClient = estClientFeminin ? 'cliente' : 'client';
  const nomClientMaj = capitaliser(nomClient);

  const estCopresidents = i2c.typePresidence === 'co-president';
  const estPresidentSeul = !estCopresidents;

  const representantsI2c = estCopresidents
    ? `ses Co-Présidents ${i2c.infoPresident1.prenom} ${i2c.infoPresident1.nom} et ${i2c.infoPresident2.prenom} ${i2c.infoPresident2.nom}`
    : `${i2c.infoPresident1.prenom} ${i2c.infoPresident1.nom}`;

  const prenomNomCopresident1 = estCopresidents
    ? `${i2c.infoPresident1.prenom} ${i2c.infoPresident1.nom}`.trim()
    : '';
  const prenomNomCopresident2 = estCopresidents
    ? `${i2c.infoPresident2.prenom} ${i2c.infoPresident2.nom}`.trim()
    : '';
  const prenomNomPresident = estPresidentSeul
    ? `${i2c.infoPresident1.prenom} ${i2c.infoPresident1.nom}`.trim()
    : '';

    const morceauxAuteur = (infosGenerales.nomAuteur || '').trim().split(/\s+/);

    const prenomAuteur = morceauxAuteur.shift() || '';
    const nomAuteur = morceauxAuteur.join(' ');

  return {
    refDocument,
    refConventionEtude,
    nomEtude: infosGenerales.nomEtude,
    anneeCreation: infosGenerales.anneeCreation,
    nombreIntervenants: formData.commun.nombreIntervenants,

    identiteClient,
    ligneSiret,
    prenomNomClient: `${client.prenom} ${client.nom}`.trim(),
    adresseClient: client.adresse,
    mailClient: client.mail,
    telClient: client.tel,
    nomEntrepriseClient: client.nomEntreprise,
    fonctionClient: client.fonction,
    detClient,
    detClientMaj,
    nomClient,
    nomClientMaj,
    detAuClient,
    prenomAuteur,
    nomAuteur,
    representantsI2c,
    estCopresidents,
    estPresidentSeul,
    prenomNomCopresident1,
    prenomNomCopresident2,
    prenomNomPresident,
  };
}